import ChevronDownThinIcon from '@assets/icons/chevron-down-v2.svg';
import ChevronRightThinIcon from '@assets/icons/chevron-right-thin.svg';
import CircleWarningIcon from '@assets/icons/circle-warning.svg';
import i18n from '@/utils/i18n';
import React, {
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { AgentAllCaughtUpState } from '@/experiences/sales-portal/domain/pricing-agent/screens/components/agent-all-caught-up-state';
import {
  AgentColumnId,
  isDeltaAgentColumn,
  isLeftAlignedAgentColumn,
  PricingRecommendationRow,
  RecommendationGroup,
} from '@/experiences/sales-portal/domain/pricing-agent/screens/types';
import { AgentGrossProfitCell } from '@/experiences/sales-portal/domain/pricing-agent/screens/components/agent-gross-profit-cell';
import { AgentInputCell } from '@/experiences/sales-portal/domain/pricing-agent/screens/components/agent-input-cell';
import { AgentNameCell } from '@/experiences/sales-portal/domain/pricing-agent/screens/components/agent-name-cell';
import { AgentReviewActions } from '@/experiences/sales-portal/domain/pricing-agent/screens/components/agent-review-actions';
import { AgentReviewFooter } from '@/experiences/sales-portal/domain/pricing-agent/screens/components/agent-review-footer';
import { AgentReviewHeader } from '@/experiences/sales-portal/domain/pricing-agent/screens/components/agent-review-header';
import { AgentTableHeaderCells } from '@/experiences/sales-portal/domain/pricing-agent/screens/components/agent-table-header-cells';
import { AgentTableRow } from '@/experiences/sales-portal/domain/pricing-agent/screens/components/agent-table-row';
import { AgentWeeklyGPImpactCell } from '@/experiences/sales-portal/domain/pricing-agent/screens/components/agent-weekly-gp-impact-cell';
import { applyPricingFilters } from '@sales-portal/domain/pricing/blocs/utils/pricing-filters';
import { AppPageLayout } from '@experiences/templates/app-page-layout';
import {
  bookmarkKey,
  usePricingBookmarksStore,
} from '@sales-portal/domain/pricing/blocs/stores/pricing-bookmarks-store';
import { buildAgentFilterSections } from '@/experiences/sales-portal/domain/pricing-agent/blocs/utils/pricing-agent-filter-sections';
import { Button, ButtonVariant } from '@sales-portal/components/button';
import { CollapsibleStickyHeaderRow } from '@/experiences/sales-portal/domain/pricing/components/collapsible-sticky-header-row';
import {
  COLUMN_WIDTH,
  PRICING_CONTENT_MIN_WIDTH,
  PRICING_FILTER_PANEL_WIDTH,
  PRICING_TABLE_LAYOUT,
} from '@sales-portal/domain/pricing/constants';
import {
  ColumnDef,
  createColumnHelper,
  ExpandedState,
  FilterFn,
  OnChangeFn,
  Row,
  SortingState,
} from '@tanstack/react-table';
import { createStyles } from '@/contexts/theme-context';
import { EmptyState } from '@experiences/components/empty-state';
import {
  formatCurrency,
  getCurrencySymbol,
  MICRO_CONVERSION,
} from '@/utils/money';
import { formatPercentage } from '@sales-portal/blocs/utils/money';
import { getRenderedRowIndex } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/components/order-entry-table/order-entry-table-utils';
import { LayoutChangeEvent, View } from 'react-native';
import { leafDimensionOf } from '@/experiences/sales-portal/domain/pricing/blocs/utils/pricing-grouping';
import { PricingAgentHelpIconButton } from '@sales-portal/domain/pricing-agent/screens/components/pricing-agent-help-icon-button';
import { PricingAgentHelpModal } from '@sales-portal/domain/pricing-agent/screens/components/pricing-agent-help-modal';
import {
  PricingAnalyticEvent,
  pricingManagementAnalytics,
  PricingPageSource,
} from '@/experiences/sales-portal/domain/pricing/analytics/pricing-management-analytics';
import { PricingBreadcrumbs } from '@sales-portal/domain/pricing/components/pricing-breadcrumbs';
import { PricingEditField } from '@sales-portal/domain/pricing/blocs/stores/pricing-edits-store-types';
import { PricingFilterButton } from '@sales-portal/domain/pricing/components/pricing-filter-button';
import { PricingFilterEmptyState } from '@sales-portal/domain/pricing/components/pricing-filter-empty-state';
import { PricingFilterPanel } from '@sales-portal/domain/pricing/components/pricing-filter-panel';
import {
  PricingFilterScope,
  PricingFilterUnit,
  PricingRangeColumnConfig,
} from '@sales-portal/domain/pricing/blocs/utils/pricing-filter-types';
import { PricingGroupByToggleButton } from '@/experiences/sales-portal/domain/pricing/components/pricing-group-by-toggle-button';
import { PricingSearchBar } from '@sales-portal/domain/pricing/components/pricing-search-bar';
import { PricingTableDeltaCell } from '@sales-portal/domain/pricing/screens/management/components/pricing-table-delta-cell';
import { PricingTableSkeleton } from '@sales-portal/domain/pricing/screens/management/components/pricing-table-skeleton';
import {
  recommendationLabelOf,
  toGroups,
  useRecommendationTransformer,
} from '@/experiences/sales-portal/domain/pricing-agent/blocs/transformers/use-recommendation-transformer';
import {
  RenderHeaderRowCallback,
  RenderRowCallback,
} from '@experiences/components/table/types';
import { resolveDelta } from '@sales-portal/domain/pricing/screens/management/blocs/utils/table-ui';
import { resolveEffectiveAction } from '@/experiences/sales-portal/domain/pricing-agent/blocs/utils/effective-recommendation-action';
import { RouteProp, useNavigation, useRoute } from '@react-navigation/native';
import { SALES_PORTAL_ROUTES } from '@/navigators/routes';
import { SalesHubShellTemplate } from '@/experiences/sales-portal/templates/sales-hub-shell-template';
import { SalesPortalStackParams } from '@/navigators/types';
import { SearchInput } from '@sales-portal/components/search-input';
import { SearchSkeleton } from '@sales-portal/components/search-skeleton';
import { StackNavigationProp } from '@react-navigation/stack';
import { Table } from '@experiences/components/table/table';
import { TableName } from '@/domain/configuration/blocs/stores/column-preferences-keys';
import { Typography } from '@usepepper/ui/atoms/typography';
import { useAutoPublishStatusSync } from '@/experiences/sales-portal/domain/pricing-agent/blocs/hooks/use-auto-publish-status-sync';
import { useBusinessOrganizationPricingConfig } from '@/experiences/sales-portal/blocs/hooks/use-business-organization-pricing-config';
import { usePricingEditsTimezoneSync } from '@sales-portal/domain/pricing/screens/management/blocs/hooks/use-pricing-edits-timezone-sync';
import { usePricingFilterPanel } from '@sales-portal/domain/pricing/blocs/hooks/use-pricing-filter-panel';
import { usePricingFilters } from '@sales-portal/domain/pricing/blocs/hooks/use-pricing-filters';
import { usePricingFiltersUrlSync } from '@sales-portal/domain/pricing/blocs/hooks/use-pricing-filters-url-sync';
import { usePricingGroupBy } from '@/experiences/sales-portal/domain/pricing/blocs/hooks/use-pricing-group-by';
import { usePricingRecommendationsLoader } from '@/experiences/sales-portal/domain/pricing-agent/blocs/loaders/use-pricing-recommendations-loader';
import { usePricingSearch } from '@/experiences/sales-portal/domain/pricing/blocs/hooks/use-pricing-search';
import { usePricingTableUIStore } from '@sales-portal/domain/pricing/blocs/stores/pricing-table-ui-store';
import { usePublishRecommendations } from '@/experiences/sales-portal/domain/pricing-agent/blocs/hooks/use-publish-recommendations';
import { useRecommendationActionsStore } from '@/experiences/sales-portal/domain/pricing-agent/blocs/stores/recommendation-actions-store';

type AgentReviewRow = RecommendationGroup | PricingRecommendationRow;

const columnHelper = createColumnHelper<AgentReviewRow>();

const NAME_COLUMN_SIZE = {
  maxSize: 340,
  minSize: COLUMN_WIDTH.M.minSize,
};

const FILTER_COLUMN_IDS: string[] = [
  AgentColumnId.UOM,
  AgentColumnId.GrossMarginPercentage,
  AgentColumnId.Price,
  AgentColumnId.GrossProfit,
  AgentColumnId.WeeklyVolume,
  AgentColumnId.WeeklyGPImpact,
];

const isRecommendationRow = (
  row: AgentReviewRow,
): row is PricingRecommendationRow => !('subRows' in row);

const agentReviewGlobalFilterFn: FilterFn<AgentReviewRow> = (
  row,
  _columnId,
  filterValue,
) => {
  const searchTerm = (filterValue as string).toLowerCase();
  const original = row.original;
  const fields = isRecommendationRow(original)
    ? [
        original.variantPackName,
        original.variantPackExternalId,
        original.restaurantName,
        original.restaurantAccountId,
      ]
    : [original.groupName, original.groupId];
  return fields.some((field) => field.toLowerCase().includes(searchTerm));
};

interface Props {
  lastCompletedAt?: string;
  taskName?: string;
  taskTypeUUID: string;
}

export const PricingAgentReviewScreen: React.FC<Props> = ({
  lastCompletedAt,
  taskName,
  taskTypeUUID,
}) => {
  const navigation =
    useNavigation<StackNavigationProp<SalesPortalStackParams>>();
  const route =
    useRoute<
      RouteProp<
        SalesPortalStackParams,
        typeof SALES_PORTAL_ROUTES.PricingAgentReview
      >
    >();
  const [styles, theme] = useStyles();
  const [tableLayoutWidth, setTableLayoutWidth] = useState<number | null>(null);
  const handleTableLayout = useCallback(
    (event: LayoutChangeEvent) =>
      setTableLayoutWidth(event.nativeEvent.layout.width),
    [],
  );
  const { hasBookmarkingAccess, isHistoricalPricingContextEnabled } =
    useBusinessOrganizationPricingConfig();
  const { clearSearch, debouncedSearchText, searchText, setSearchText } =
    usePricingSearch();
  const [sorting, setSorting] = useState<SortingState>([
    { desc: true, id: AgentColumnId.WeeklyGPImpact },
  ]);
  const [expanded, setExpanded] = useState<ExpandedState>(true);
  const [showHelpModal, setShowHelpModal] = useState(false);

  const hasSeenAgentHelpModal = usePricingTableUIStore(
    (state) => state.hasSeenAgentHelpModal,
  );
  const markAgentHelpModalSeen = usePricingTableUIStore(
    (state) => state.markAgentHelpModalSeen,
  );

  const { currencyCode } = usePricingEditsTimezoneSync();

  // taskName rides in on the nav param from the list; a direct load/refresh has
  // no param, so fall back to the generic title.
  const title = taskName ?? i18n.t('pricing.agent.review.title');

  const syncTaskType = useRecommendationActionsStore(
    (state) => state.syncTaskType,
  );
  const pruneStaleRecommendations = useRecommendationActionsStore(
    (state) => state.pruneStaleRecommendations,
  );

  useEffect(() => {
    syncTaskType(taskTypeUUID);
  }, [syncTaskType, taskTypeUUID]);

  const { data, error, loading, refetch } = usePricingRecommendationsLoader({
    taskTypeUUID,
  });
  const { groupBy, setGroupBy } = usePricingGroupBy(TableName.PricingAgent);

  const toggleGroupBy = useCallback(() => {
    const next = leafDimensionOf(groupBy);
    setGroupBy(next);
    // The parents are different rows now, so start the new view expanded.
    setExpanded(true);
    pricingManagementAnalytics.track(PricingAnalyticEvent.GROUP_BY_TOGGLE, {
      groupBy: next,
      pageSource: PricingPageSource.AGENT_REVIEW,
    });
  }, [groupBy, setGroupBy]);

  const {
    autoPublishAt,
    groups,
    hasRecommendations,
    isAutoPublish,
    isPublishingEnabled,
    progress,
    rows,
  } = useRecommendationTransformer(data, groupBy);

  // `progress` and `recommendations` can disagree on visibility (e.g. role
  // scoping), so only treat this as "all caught up" when there's actually
  // reviewed work to show — otherwise there's nothing to review here.
  const hasReviewedAny = progress
    ? progress.accepted + progress.overridden + progress.rejected > 0
    : false;

  const { flushPendingSync } = useAutoPublishStatusSync({
    enabled: isAutoPublish,
    rows,
    taskTypeUUID,
  });

  const { isPublishing, publish } = usePublishRecommendations({
    flushPendingSync,
    taskTypeUUID,
  });

  useEffect(() => {
    if (loading || error || !data) {
      return;
    }
    pruneStaleRecommendations(rows.map((row) => row.uuid));
  }, [data, error, loading, pruneStaleRecommendations, rows]);

  useEffect(() => {
    if (!loading && !error && hasRecommendations && !hasSeenAgentHelpModal) {
      setShowHelpModal(true);
      markAgentHelpModalSeen();
    }
  }, [
    error,
    hasRecommendations,
    hasSeenAgentHelpModal,
    loading,
    markAgentHelpModalSeen,
  ]);

  const hasTrackedLoad = useRef(false);
  useEffect(() => {
    if (!loading && !error && data && !hasTrackedLoad.current) {
      hasTrackedLoad.current = true;
      pricingManagementAnalytics.track(
        PricingAnalyticEvent.LOAD_AGENT_REVIEW_PAGE,
        {},
      );
    }
  }, [data, error, loading]);

  const bookmarks = usePricingBookmarksStore((state) => state.bookmarks);
  const bookmarkedByRecommendation = useMemo(() => {
    if (!hasBookmarkingAccess) {
      return undefined;
    }
    return rows.reduce<Record<string, boolean>>((acc, row) => {
      acc[row.uuid] =
        bookmarkKey(row.variantPackUUID, row.restaurantUUID) in bookmarks;
      return acc;
    }, {});
  }, [bookmarks, hasBookmarkingAccess, rows]);

  const localActions = useRecommendationActionsStore((state) => state.actions);
  const getEffectiveStatus = useCallback(
    (row: PricingRecommendationRow) =>
      resolveEffectiveAction(
        localActions[row.uuid]?.action,
        row.reviewStatus,
        isAutoPublish,
      ),
    [isAutoPublish, localActions],
  );

  const rangeColumnConfigs = useMemo<
    Record<string, PricingRangeColumnConfig<PricingRecommendationRow>>
  >(() => {
    const currencyAffix = getCurrencySymbol(currencyCode);

    return {
      [AgentColumnId.GrossMarginPercentage]: {
        accessor: (row) =>
          localActions[row.uuid]?.overrideGrossProfitInPercentage ??
          row.grossProfitInPercentage,
        affix: '%',
        title: i18n.t('pricing.agent.review.rec_gross_margin_percentage'),
        unit: PricingFilterUnit.Percent,
      },
      [AgentColumnId.GrossProfit]: {
        accessor: (row) =>
          ((localActions[row.uuid]?.overridePriceInMicros ??
            row.newPriceInMicros) -
            row.snapshotCostInMicros) /
          MICRO_CONVERSION,
        affix: currencyAffix,
        title: i18n.t('pricing.agent.review.rec_gross_profit'),
        unit: PricingFilterUnit.Currency,
      },
      [AgentColumnId.Price]: {
        accessor: (row) =>
          (localActions[row.uuid]?.overridePriceInMicros ??
            row.newPriceInMicros) / MICRO_CONVERSION,
        affix: currencyAffix,
        title: i18n.t('pricing.agent.review.rec_price'),
        unit: PricingFilterUnit.Currency,
      },
      [AgentColumnId.WeeklyGPImpact]: {
        accessor: (row) =>
          row.weeklyGPImpactInMicros === null
            ? null
            : row.weeklyGPImpactInMicros / MICRO_CONVERSION,
        affix: currencyAffix,
        title: i18n.t('pricing.agent.review.rec_gross_profit_impact'),
        unit: PricingFilterUnit.Currency,
      },
      [AgentColumnId.WeeklyVolume]: {
        accessor: (row) => row.weeklyVolume,
        affix: 'u',
        title: i18n.t('pricing.table.header.weekly_volume'),
        unit: PricingFilterUnit.Unit,
      },
    };
  }, [currencyCode, localActions]);

  const filterSections = useMemo(
    () =>
      buildAgentFilterSections({
        bookmarkedByRecommendation,
        columnIds: FILTER_COLUMN_IDS,
        getEffectiveStatus,
        includeUnreviewed: !isAutoPublish,
        rangeColumnConfigs,
        rows,
      }),
    [
      bookmarkedByRecommendation,
      getEffectiveStatus,
      isAutoPublish,
      rangeColumnConfigs,
      rows,
    ],
  );

  const filters = usePricingFilters(
    PricingFilterScope.AgentReview,
    filterSections,
  );
  const panel = usePricingFilterPanel();

  const handleFiltersUrlUpdate = useCallback(
    (params: Record<string, string | undefined>) =>
      navigation.setParams(params),
    [navigation],
  );

  usePricingFiltersUrlSync({
    hydrate: filters.hydrate,
    onUrlUpdate: handleFiltersUrlUpdate,
    routeParams: route.params,
    sections: filterSections,
    selections: { pills: filters.pills, ranges: filters.ranges },
  });

  useEffect(() => {
    filters.pruneSections(filterSections.map((section) => section.id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterSections, filters.pruneSections]);

  const { pills: filterPills, ranges: filterRanges } = filters;

  const filteredGroups = useMemo(() => {
    const filteredRows = applyPricingFilters(
      rows,
      { pills: filterPills, ranges: filterRanges },
      filterSections,
    );
    return filteredRows.length === rows.length
      ? groups
      : toGroups(filteredRows, groupBy);
  }, [filterPills, filterRanges, groupBy, groups, rows, filterSections]);

  const handleClearEmptyState = useCallback(() => {
    if (debouncedSearchText.length > 0) {
      clearSearch();
    } else {
      filters.clearAll();
    }
  }, [clearSearch, debouncedSearchText, filters]);

  const onSortingChange = useCallback<OnChangeFn<SortingState>>(
    (next) => setSorting(next),
    [],
  );

  const onExpandedChange = useCallback<OnChangeFn<ExpandedState>>(
    (next) => setExpanded(next),
    [],
  );

  const columns = useMemo<ColumnDef<AgentReviewRow, string>[]>(() => {
    const reasonColumnPercentage = 30;
    const remainingColumns = 7;
    const columnWidthPercentage =
      (100 -
        PRICING_TABLE_LAYOUT.NAME_COLUMN_PERCENTAGE -
        reasonColumnPercentage) /
      remainingColumns;

    return [
      columnHelper.accessor(
        (row) =>
          isRecommendationRow(row)
            ? recommendationLabelOf(row, leafDimensionOf(groupBy)).name
            : row.groupName,
        {
          cell: ({ getValue, row }) => {
            if (row.depth === 0) {
              return (
                <View style={styles.groupNameWrapper}>
                  {row.getIsExpanded() ? (
                    <ChevronDownThinIcon size={16} stroke="black" />
                  ) : (
                    <ChevronRightThinIcon size={16} stroke="black" />
                  )}
                  <Typography numberOfLines={1} variant="paragraph-md-600">
                    {getValue()}
                  </Typography>
                </View>
              );
            }
            return (
              <AgentNameCell
                defaultAccepted={isAutoPublish}
                name={getValue()}
                row={row.original as PricingRecommendationRow}
              />
            );
          },
          header: () => (
            <Typography
              color={theme.sk.color.text.tertiary}
              numberOfLines={1}
              variant="paragraph-sm-600"
            >
              {i18n.t('pricing.table.header.name')}
            </Typography>
          ),
          id: AgentColumnId.Name,
          meta: {
            initialPercentWidth: PRICING_TABLE_LAYOUT.NAME_COLUMN_PERCENTAGE,
          },
          ...NAME_COLUMN_SIZE,
          sortingFn: 'alphanumeric',
        },
      ),
      columnHelper.accessor(
        (row) =>
          isRecommendationRow(row)
            ? recommendationLabelOf(row, leafDimensionOf(groupBy)).id
            : row.groupId,
        {
          cell: ({ getValue }) => {
            return (
              <Typography
                color={theme.sk.color.text.primary}
                variant="paragraph-md"
              >
                {getValue()}
              </Typography>
            );
          },
          header: () => (
            <Typography
              color={theme.sk.color.text.tertiary}
              variant="paragraph-sm-600"
            >
              {i18n.t('pricing.table.header.id')}
            </Typography>
          ),
          id: AgentColumnId.Id,
          meta: { initialPercentWidth: columnWidthPercentage },
          ...COLUMN_WIDTH.M,
          sortingFn: 'alphanumeric',
        },
      ),
      columnHelper.accessor(
        (row) => (isRecommendationRow(row) ? row.variantPackUOM : ''),
        {
          cell: ({ getValue }) => (
            <Typography
              color={theme.sk.color.text.primary}
              textTransform="capitalize"
              variant="paragraph-md"
            >
              {getValue()}
            </Typography>
          ),
          header: () => (
            <Typography
              color={theme.sk.color.text.tertiary}
              variant="paragraph-sm-600"
            >
              {i18n.t('pricing.table.header.uom')}
            </Typography>
          ),
          id: AgentColumnId.UOM,
          meta: { initialPercentWidth: columnWidthPercentage },
          ...COLUMN_WIDTH.M,
          sortingFn: 'alphanumeric',
        },
      ),
      columnHelper.accessor(
        (row) =>
          isRecommendationRow(row)
            ? formatPercentage(row.grossProfitInPercentage, 1, 1)
            : '',
        {
          cell: ({ row, ...context }) => {
            if (row.depth === 0) {
              return null;
            }
            const original = row.original as PricingRecommendationRow;
            return (
              <AgentInputCell
                canBookmarkItem={hasBookmarkingAccess}
                columnId={context.column.id}
                currencyCode={currencyCode}
                disabled={isPublishing}
                inputType={PricingEditField.GrossMarginPercentage}
                isHistoricalPricingContextEnabled={
                  isHistoricalPricingContextEnabled
                }
                row={original}
                tableRowIndex={getRenderedRowIndex(context, row.index)}
              />
            );
          },
          header: () => (
            <Typography
              color={theme.sk.color.text.tertiary}
              variant="paragraph-sm-600"
            >
              {i18n.t('pricing.agent.review.rec_gross_margin_percentage')}
            </Typography>
          ),
          id: AgentColumnId.GrossMarginPercentage,
          meta: { initialPercentWidth: columnWidthPercentage },
          ...COLUMN_WIDTH.M,
          sortingFn: (rowA, rowB) => {
            const a = isRecommendationRow(rowA.original)
              ? rowA.original.grossProfitInPercentage
              : 0;
            const b = isRecommendationRow(rowB.original)
              ? rowB.original.grossProfitInPercentage
              : 0;
            return a - b;
          },
        },
      ),
      columnHelper.accessor(
        (row) =>
          isRecommendationRow(row)
            ? formatCurrency(row.newPriceInMicros, currencyCode)
            : '',
        {
          cell: ({ row, ...context }) => {
            if (row.depth === 0) {
              return null;
            }
            const original = row.original as PricingRecommendationRow;
            return (
              <AgentInputCell
                canBookmarkItem={hasBookmarkingAccess}
                columnId={context.column.id}
                currencyCode={currencyCode}
                disabled={isPublishing}
                inputType={PricingEditField.Price}
                isHistoricalPricingContextEnabled={
                  isHistoricalPricingContextEnabled
                }
                row={original}
                tableRowIndex={getRenderedRowIndex(context, row.index)}
              />
            );
          },
          header: () => (
            <Typography
              color={theme.sk.color.text.tertiary}
              variant="paragraph-sm-600"
            >
              {i18n.t('pricing.agent.review.rec_price')}
            </Typography>
          ),
          id: AgentColumnId.Price,
          meta: { initialPercentWidth: columnWidthPercentage },
          ...COLUMN_WIDTH.M,
          sortingFn: (rowA, rowB) => {
            const a = isRecommendationRow(rowA.original)
              ? rowA.original.newPriceInMicros
              : 0;
            const b = isRecommendationRow(rowB.original)
              ? rowB.original.newPriceInMicros
              : 0;
            return a - b;
          },
        },
      ),
      columnHelper.accessor(
        (row) => {
          if (!isRecommendationRow(row)) {
            return '';
          }
          const grossProfit = row.newPriceInMicros - row.snapshotCostInMicros;
          return formatCurrency(grossProfit, currencyCode);
        },
        {
          cell: ({ row }) => {
            if (row.depth === 0) {
              return null;
            }
            const original = row.original as PricingRecommendationRow;
            return (
              <AgentGrossProfitCell
                currencyCode={currencyCode}
                row={original}
              />
            );
          },
          header: () => (
            <Typography
              color={theme.sk.color.text.tertiary}
              variant="paragraph-sm-600"
            >
              {i18n.t('pricing.agent.review.rec_gross_profit')}
            </Typography>
          ),
          id: AgentColumnId.GrossProfit,
          meta: { initialPercentWidth: columnWidthPercentage },
          ...COLUMN_WIDTH.S,
          sortingFn: (rowA, rowB) => {
            const aGP = isRecommendationRow(rowA.original)
              ? rowA.original.newPriceInMicros -
                rowA.original.snapshotCostInMicros
              : 0;
            const bGP = isRecommendationRow(rowB.original)
              ? rowB.original.newPriceInMicros -
                rowB.original.snapshotCostInMicros
              : 0;
            return aGP - bGP;
          },
        },
      ),
      columnHelper.accessor(
        (row) => {
          if (isRecommendationRow(row)) {
            return (row.weeklyVolume ?? 0).toFixed(1);
          }
          return row.aggregateWeeklyVolume.toFixed(1);
        },
        {
          cell: ({ getValue }) => (
            <Typography variant="paragraph-md">{getValue()}</Typography>
          ),
          header: () => (
            <Typography
              color={theme.sk.color.text.tertiary}
              variant="paragraph-sm-600"
            >
              {i18n.t('pricing.table.header.weekly_volume')}
            </Typography>
          ),
          id: AgentColumnId.WeeklyVolume,
          meta: { initialPercentWidth: columnWidthPercentage },
          ...COLUMN_WIDTH.XS,
          sortingFn: (rowA, rowB) => {
            const a = isRecommendationRow(rowA.original)
              ? (rowA.original.weeklyVolume ?? 0)
              : rowA.original.aggregateWeeklyVolume;
            const b = isRecommendationRow(rowB.original)
              ? (rowB.original.weeklyVolume ?? 0)
              : rowB.original.aggregateWeeklyVolume;
            return a - b;
          },
        },
      ),
      columnHelper.accessor(
        (row) => {
          const value = isRecommendationRow(row)
            ? (row.weeklyGPImpactInMicros ?? 0)
            : row.aggregateWeeklyGPImpactInMicros;
          return formatCurrency(value, currencyCode);
        },
        {
          cell: ({ row }) => {
            if (isRecommendationRow(row.original)) {
              return (
                <AgentWeeklyGPImpactCell
                  currencyCode={currencyCode}
                  row={row.original}
                />
              );
            }
            const { direction, formattedValue } = resolveDelta(
              row.original.aggregateWeeklyGPImpactInMicros,
              (v) => formatCurrency(v, currencyCode),
            );
            return (
              <PricingTableDeltaCell
                arrowDirection={direction}
                value={formattedValue}
              />
            );
          },
          header: () => (
            <Typography
              color={theme.sk.color.text.tertiary}
              variant="paragraph-sm-600"
            >
              {i18n.t('pricing.agent.review.rec_gross_profit_impact')}
            </Typography>
          ),
          id: AgentColumnId.WeeklyGPImpact,
          meta: { initialPercentWidth: columnWidthPercentage },
          ...COLUMN_WIDTH.M,
          sortingFn: (rowA, rowB) => {
            const a = isRecommendationRow(rowA.original)
              ? (rowA.original.weeklyGPImpactInMicros ?? 0)
              : rowA.original.aggregateWeeklyGPImpactInMicros;
            const b = isRecommendationRow(rowB.original)
              ? (rowB.original.weeklyGPImpactInMicros ?? 0)
              : rowB.original.aggregateWeeklyGPImpactInMicros;
            return a - b;
          },
        },
      ),
      columnHelper.accessor(
        (row) => (isRecommendationRow(row) ? row.reasoning : ''),
        {
          cell: ({ row, getValue }) => {
            if (row.depth === 0) {
              return null;
            }
            return (
              <Typography
                color={theme.sk.color.text.primary}
                numberOfLines={3}
                variant="paragraph-sm"
              >
                {getValue()}
              </Typography>
            );
          },
          enableSorting: false,
          header: () => (
            <Typography
              color={theme.sk.color.text.tertiary}
              variant="paragraph-sm-600"
            >
              {i18n.t('pricing.agent.review.recommendation_reason')}
            </Typography>
          ),
          id: AgentColumnId.Reason,
          meta: { initialPercentWidth: reasonColumnPercentage },
        },
      ),
    ];
  }, [
    currencyCode,
    groupBy,
    hasBookmarkingAccess,
    isAutoPublish,
    isHistoricalPricingContextEnabled,
    isPublishing,
    styles,
    theme,
  ]);

  const renderHeaderRow = useCallback<RenderHeaderRowCallback<AgentReviewRow>>(
    ({ headerGroup }) => (
      <AgentTableHeaderCells
        headerCellStyle={styles.headerCell}
        headerGroup={headerGroup}
        isLeftAlignedColumn={isLeftAlignedAgentColumn}
        key={headerGroup.id}
        sorting={sorting}
      />
    ),
    [sorting, styles.headerCell],
  );

  // A fresh array here would churn the table context and every cell in it.
  const cellPadding = useMemo(
    () => Array(columns.length).fill(theme.sk.spacing.spaceSmall),
    [columns.length, theme.sk.spacing.spaceSmall],
  );

  const isStickyRow = useCallback(
    (row: Row<AgentReviewRow>) => row.depth === 0,
    [],
  );

  const renderRow = useCallback<RenderRowCallback<AgentReviewRow>>(
    ({ index, row }) =>
      row.depth === 0 ? (
        <CollapsibleStickyHeaderRow
          index={index}
          isDeltaColumn={isDeltaAgentColumn}
          isLeftAlignedColumn={isLeftAlignedAgentColumn}
          key={row.id}
          onPress={row.getToggleExpandedHandler()}
          row={row}
          testID={`agent-group-header-row-${row.index}`}
        />
      ) : (
        <AgentTableRow
          defaultAccepted={isAutoPublish}
          disabled={isPublishing}
          index={index}
          key={row.id}
          row={row}
        />
      ),
    [isAutoPublish, isPublishing],
  );

  const LoadingSkeleton = () => (
    <View
      style={styles.loadingContent}
      testID="pricing-agent-review-loading-skeleton"
    >
      <SearchSkeleton />
      <PricingTableSkeleton />
    </View>
  );

  return (
    <SalesHubShellTemplate>
      <AppPageLayout
        actions={
          hasRecommendations ? (
            <View
              style={panel.isOpen ? styles.actionsWithPanelOpen : undefined}
            >
              <AgentReviewActions
                autoPublishAt={autoPublishAt}
                isPublishing={isPublishing}
                isPublishingEnabled={isPublishingEnabled}
                onPublish={publish}
                rows={rows}
              />
            </View>
          ) : undefined
        }
        leadingActions={
          <PricingBreadcrumbs
            parentRoute={SALES_PORTAL_ROUTES.PricingAgentTasks}
            secondaryBreadcrumb={i18n.t('pricing.agent.sidenav_title')}
            tertiaryBreadcrumb={title}
          />
        }
        overlay={
          panel.isOpen ? (
            <PricingFilterPanel
              filters={filters}
              onClose={panel.close}
              sections={filterSections}
            />
          ) : null
        }
        scroll={false}
      >
        <View
          style={[
            styles.container,
            panel.isOpen && styles.containerWithPanelOpen,
          ]}
        >
          {loading ? (
            <LoadingSkeleton />
          ) : error ? (
            <EmptyState
              bottomElement={
                <Button
                  accessibilityLabel={i18n.t('sales_portal.error.retry')}
                  onPress={() => refetch()}
                  variant={ButtonVariant.Secondary}
                  width={120}
                >
                  {i18n.t('sales_portal.error.retry')}
                </Button>
              }
              description={i18n.t('pricing.error.table.description')}
              iconElement={
                <CircleWarningIcon stroke={theme.sk.color.icon.primary} />
              }
              title={i18n.t('pricing.error.table.title')}
            />
          ) : !hasRecommendations && progress && hasReviewedAny ? (
            <View style={styles.allCaughtUpWrapper}>
              <AgentAllCaughtUpState
                acceptedCount={progress.accepted}
                overriddenCount={progress.overridden}
                rejectedCount={progress.rejected}
              />
            </View>
          ) : !hasRecommendations ? (
            <EmptyState
              description={i18n.t(
                'pricing.agent.review.no_task_runs.description',
              )}
              iconElement={
                <CircleWarningIcon stroke={theme.sk.color.icon.primary} />
              }
              title={i18n.t('pricing.agent.review.no_task_runs.title')}
            />
          ) : (
            <Suspense fallback={<LoadingSkeleton />}>
              <PricingSearchBar
                trailing={
                  <>
                    <PricingFilterButton
                      activeCount={filters.activeCount}
                      onPress={panel.open}
                    />
                    <PricingGroupByToggleButton
                      groupBy={groupBy}
                      onPress={toggleGroupBy}
                    />
                    <View style={styles.helpIconWrapper}>
                      <PricingAgentHelpIconButton
                        onPress={() => setShowHelpModal(true)}
                      />
                    </View>
                  </>
                }
              >
                <SearchInput
                  onSearchChange={setSearchText}
                  placeholderText={i18n.t(
                    'pricing.agent.review.search_placeholder',
                  )}
                  searchText={searchText}
                />
              </PricingSearchBar>
              {hasRecommendations ? (
                <AgentReviewHeader
                  currencyCode={currencyCode}
                  defaultAccepted={isAutoPublish}
                  groups={filteredGroups}
                  lastCompletedAt={lastCompletedAt ?? null}
                  title={title}
                />
              ) : null}
              <View onLayout={handleTableLayout} style={styles.tableWrapper}>
                <Table
                  bodyContainerStyle={styles.tableBody}
                  cellPadding={cellPadding}
                  columns={columns}
                  data={filteredGroups}
                  emptyComponent={
                    <PricingFilterEmptyState
                      baseEmptyComponent={null}
                      hasActiveFilters={filters.hasActiveFilters}
                      onClear={handleClearEmptyState}
                      searchText={debouncedSearchText}
                    />
                  }
                  estimatedHeaderHeight={PRICING_TABLE_LAYOUT.HEADER_HEIGHT}
                  estimatedRowHeight={PRICING_TABLE_LAYOUT.ESTIMATED_ROW_HEIGHT}
                  fillAvailableWidth
                  filterFromLeafRows
                  getSubRows={(row) =>
                    'subRows' in row ? row.subRows : undefined
                  }
                  globalFilterFn={agentReviewGlobalFilterFn}
                  headerPadding={theme.sk.spacing.spaceSmall}
                  initialState={{
                    columnPinning: { left: [AgentColumnId.Name] },
                  }}
                  isActive
                  isStickyRow={isStickyRow}
                  layoutWidth={tableLayoutWidth ?? undefined}
                  onExpandedChange={onExpandedChange}
                  onSortingChange={onSortingChange}
                  renderHeaderRow={renderHeaderRow}
                  renderRow={renderRow}
                  state={{
                    expanded,
                    globalFilter: debouncedSearchText,
                    sorting,
                  }}
                />
                {hasRecommendations && progress ? (
                  <AgentReviewFooter
                    defaultAccepted={isAutoPublish}
                    publishedProgress={progress}
                    rows={rows}
                  />
                ) : null}
              </View>
            </Suspense>
          )}
        </View>
        <PricingAgentHelpModal
          defaultAccepted={isAutoPublish}
          isOpen={showHelpModal}
          onClose={() => setShowHelpModal(false)}
        />
      </AppPageLayout>
    </SalesHubShellTemplate>
  );
};

const useStyles = createStyles((theme) => ({
  actionsWithPanelOpen: {
    paddingRight: PRICING_FILTER_PANEL_WIDTH,
  },
  allCaughtUpWrapper: {
    alignItems: 'center',
    flex: 1,
    justifyContent: 'center',
  },
  container: {
    flex: 1,
    gap: theme.sk.spacing.spaceMedium,
    paddingTop: theme.sk.spacing.spaceMedium,
  },
  containerWithPanelOpen: {
    minWidth: PRICING_CONTENT_MIN_WIDTH + PRICING_FILTER_PANEL_WIDTH,
    paddingRight: PRICING_FILTER_PANEL_WIDTH,
  },
  groupNameWrapper: {
    alignItems: 'center',
    flex: 1,
    flexDirection: 'row',
    gap: theme.sk.spacing.spaceSmall,
  },
  headerCell: {
    alignItems: 'flex-end',
    height: PRICING_TABLE_LAYOUT.HEADER_HEIGHT,
  },
  helpIconWrapper: {
    bottom: 0,
    justifyContent: 'center',
    position: 'absolute',
    right: theme.sk.spacing.spaceMedium,
    top: 0,
  },
  loadingContent: {
    flex: 1,
    gap: 42,
  },
  tableBody: {
    // Reserve room below the last row so the floating AgentReviewFooter
    // doesn't cover it when scrolled to the bottom.
    paddingBottom: 96,
  },
  tableWrapper: {
    flex: 1,
    position: 'relative',
  },
}));
